import { PermissionSet, useBusinessContext, useMobileEmbed, usePermission } from '@context'
import { fetchHolviAuthorizeUrl } from '@query/holvi'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import React from 'react'
import { Button, ButtonProps } from '@components'
import imgSrc from '@assets/Holvi_Symbol.png'
import { EmbedBankIntegrationRedirectPaths, getBankIntegrationPageURL } from '@constants'
import { getAbsoluteUrl } from '@utils/getAbsoluteUrl'
import { getMobileAwarePath } from '@utils/getMobileAwarePath'

interface HolviAuthorizeButtonProps extends ButtonProps {
  redirectUrl?: string
  label?: string
}

export const HolviAuthorizeButton: React.FC<HolviAuthorizeButtonProps> = ({
  redirectUrl,
  label,
  ...props
}) => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const canManageIntegrations = usePermission(PermissionSet.INTEGRATION_ADMIN)
  const { isEmbedded, redirectURL: embeddedRedirectUrl } = useMobileEmbed()

  const defaultRedirectPath = getAbsoluteUrl(
    getMobileAwarePath(getBankIntegrationPageURL(slug, 'holvi-finalize'), isEmbedded)
  )

  const resolvedRedirect =
    redirectUrl ??
    (isEmbedded && embeddedRedirectUrl
      ? `${embeddedRedirectUrl}/${EmbedBankIntegrationRedirectPaths.HOLVI}`
      : defaultRedirectPath)

  const { data, isLoading } = useQuery({
    queryKey: [slug, 'holvi', 'authorize', resolvedRedirect],
    queryFn: () =>
      fetchHolviAuthorizeUrl({
        slug,
        redirectUrl: resolvedRedirect
      }),
    enabled: Boolean(slug) && canManageIntegrations
  })

  return (
    <a href={data?.url} style={{ display: 'flex', width: 'fit-content' }}>
      <Button showSpinner={isLoading} icon={<img src={imgSrc} />} {...props}>
        {label ?? t('components.holviAuthorizeButton.authorize')}
      </Button>
    </a>
  )
}
